import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Button, Collapse, Badge, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import DetailList from './DetailList';
import type { AttendanceRecord } from '../types/dashboard';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

interface AttendanceChartWidgetProps {
  title: string;
  presentCount: number;
  halfDayCount: number;
  absentCount: number;
  vacationCount: number;
  holidayCount: number;
  totalCount: number;
  presentData: AttendanceRecord[];
  halfDayData: AttendanceRecord[];
  absentData: AttendanceRecord[];
  vacationData: AttendanceRecord[];
  holidayData: AttendanceRecord[];
  className?: string;
  nurseryTimezone: string;
}

const AttendanceChartWidget: React.FC<AttendanceChartWidgetProps> = ({
  title,
  presentCount,
  halfDayCount,
  absentCount,
  vacationCount,
  holidayCount,
  totalCount,
  presentData = [],
  halfDayData = [],
  absentData = [],
  vacationData = [],
  holidayData = [],
  className = '',
  nurseryTimezone
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'attendanceChartWidget' });
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const safeNumber = (value: number | undefined | null) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const calculatePercentage = (value: number, total: number) => {
    const safeValue = safeNumber(value);
    const safeTotal = safeNumber(total);
    if (safeTotal === 0) return 0;
    return Math.round((safeValue / safeTotal) * 100);
  };

  const safePresentCount = safeNumber(presentCount);
  const safeHalfDayCount = safeNumber(halfDayCount);
  const safeAbsentCount = safeNumber(absentCount);
  const safeVacationCount = safeNumber(vacationCount);
  const safeHolidayCount = safeNumber(holidayCount);
  const safeTotalCount = safeNumber(totalCount);

  // Prepare chart data for attendance donut with modern styling
  const getChartData = () => {
    return {
      labels: [t('present'), t('halfDay', { defaultValue: 'Half Day' }), t('absent'), t('vacation'), t('holiday')],
      datasets: [
        {
          data: [safePresentCount, safeHalfDayCount, safeAbsentCount, safeVacationCount, safeHolidayCount],
          backgroundColor: [
            'rgba(40, 167, 69, 0.9)',
            'rgba(255, 193, 7, 0.9)',
            'rgba(220, 53, 69, 0.9)',
            'rgba(13, 202, 240, 0.9)',
            'rgba(0, 150, 136, 0.9)'
          ],
          borderColor: [
            'rgba(40, 167, 69, 1)',
            'rgba(255, 193, 7, 1)',
            'rgba(220, 53, 69, 1)',
            'rgba(13, 202, 240, 1)',
            'rgba(0, 150, 136, 1)'
          ],
          borderWidth: 4,
          hoverBackgroundColor: [
            'rgba(40, 167, 69, 1)',
            'rgba(255, 193, 7, 1)',
            'rgba(220, 53, 69, 1)',
            'rgba(13, 202, 240, 1)',
            'rgba(0, 150, 136, 1)'
          ],
          hoverBorderColor: [
            'rgba(255, 255, 255, 1)',
            'rgba(255, 255, 255, 1)',
            'rgba(255, 255, 255, 1)',
            'rgba(255, 255, 255, 1)',
            'rgba(255, 255, 255, 1)'
          ],
          hoverBorderWidth: 6,
          borderRadius: 8,
          spacing: 4,
          weight: 1
        },
      ],
    };
  };

  const getStatusColor = (percentage: number) => {
    if (percentage >= 90) return 'success';
    if (percentage >= 70) return 'info';
    if (percentage >= 50) return 'warning';
    return 'danger';
  };

  const presentPercentage = calculatePercentage(safePresentCount, safeTotalCount);
  const halfDayPercentage = calculatePercentage(safeHalfDayCount, safeTotalCount);
  const absentPercentage = calculatePercentage(safeAbsentCount, safeTotalCount);
  const vacationPercentage = calculatePercentage(safeVacationCount, safeTotalCount);
  const holidayPercentage = calculatePercentage(safeHolidayCount, safeTotalCount);
  const displayedTotal = safePresentCount + safeHalfDayCount + safeAbsentCount + safeVacationCount + safeHolidayCount;

  // Custom plugin for modern center text display
  const modernCenterTextPlugin = {
    id: 'modernCenterText',
    beforeDraw: function(chart: any) {
      const { ctx, chartArea: { left, top, width, height } } = chart;
      
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      const centerX = left + width / 2;
      const centerY = top + height / 2;
      
      const gradient = ctx.createLinearGradient(centerX - 50, centerY - 30, centerX + 50, centerY + 30);
      gradient.addColorStop(0, '#495057');
      gradient.addColorStop(1, '#6c757d');
      
      ctx.font = 'bold 28px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = gradient;
      ctx.fillText(safeTotalCount.toString(), centerX, centerY - 15);
      
      ctx.font = '600 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#6c757d';
      ctx.fillText(t('totalChildren'), centerX, centerY + 8);
      
      ctx.font = '600 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#28a745';
      ctx.fillText(t('percentPresent', { percentage: presentPercentage }), centerX, centerY + 28);
      
      ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
      ctx.shadowBlur = 4;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 2;
      
      ctx.restore();
    }
  };

  return (
    <div className={`attendance-chart-widget ${className}`}>
      <div className="p-3">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="flex-grow-1">
            <small className="text-dark d-block mb-1 fw-bold" style={{ fontSize: '1rem' }}>
              {title}
            </small>
            <div className="d-flex align-items-center gap-2">
              <h4 className="mb-0" style={{ color: theme.palette.text.secondary }}>
                {displayedTotal}
              </h4>
              <Badge bg="info" className="px-2 py-1" style={{ fontSize: '0.75rem' }}>
                {t('totalChildren')}
              </Badge>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="link"
              size="sm"
              className="p-0 text-muted"
              onClick={toggleExpanded}
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <i className="bi bi-chevron-up" style={{ fontSize: '1rem' }}></i>
              ) : (
                <i className="bi bi-chevron-down" style={{ fontSize: '1rem' }}></i>
              )}
            </Button>
          </div>
        </div>

        {/* Main Content - Left Text, Right Chart */}
        <Row className="align-items-center" style={{ minHeight: '200px' }}>
              {/* Left Side - Present/Absent/Vacation/Holiday Information */}
          <Col xs={12} md={6} className="text-center text-md-start">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              {/* Present Section */}
              <div className="text-center flex-fill">
                <div className="text-success">
                  <h2 className="mb-1 fw-bold" style={{ fontSize: '2.2rem', lineHeight: '1' }}>
                    {safePresentCount}
                  </h2>
                  <h6 className="mb-2 text-success fw-bold">{t('present')}</h6>
                  <Badge 
                    bg={getStatusColor(presentPercentage)} 
                    className="px-2 py-1" 
                    style={{ fontSize: '0.9rem' }}
                  >
                    {presentPercentage}%
                  </Badge>
                </div>
              </div>

              {/* Holiday Section */}
              <div className="text-center flex-fill">
                <div style={{ color: '#009688' }}>
                  <h2 className="mb-1 fw-bold" style={{ fontSize: '2.2rem', lineHeight: '1' }}>
                    {safeHolidayCount}
                  </h2>
                  <h6 className="mb-2 fw-bold" style={{ color: '#009688' }}>{t('holiday')}</h6>
                  <Badge 
                    bg="success" 
                    className="px-2 py-1" 
                    style={{ fontSize: '0.9rem', backgroundColor: '#009688' }}
                  >
                    {holidayPercentage}%
                  </Badge>
                </div>
              </div>

              {/* Half Day Section */}
              <div className="text-center flex-fill">
                <div className="text-warning">
                  <h2 className="mb-1 fw-bold" style={{ fontSize: '2.2rem', lineHeight: '1' }}>
                    {safeHalfDayCount}
                  </h2>
                  <h6 className="mb-2 text-warning fw-bold">{t('halfDay', { defaultValue: 'Half Day' })}</h6>
                  <Badge
                    bg="warning"
                    className="px-2 py-1"
                    style={{ fontSize: '0.9rem' }}
                  >
                    {halfDayPercentage}%
                  </Badge>
                </div>
              </div>

              {/* Absent Section */}
              <div className="text-center flex-fill">
                <div className="text-danger">
                  <h2 className="mb-1 fw-bold" style={{ fontSize: '2.2rem', lineHeight: '1' }}>
                    {safeAbsentCount}
                  </h2>
                  <h6 className="mb-2 text-danger fw-bold">{t('absent')}</h6>
                  <Badge 
                    bg={getStatusColor(absentPercentage)} 
                    className="px-2 py-1" 
                    style={{ fontSize: '0.9rem' }}
                  >
                    {absentPercentage}%
                  </Badge>
                </div>
              </div>

              {/* Vacation Section */}
              <div className="text-center flex-fill">
                <div className="text-warning">
                  <h2 className="mb-1 fw-bold" style={{ fontSize: '2.2rem', lineHeight: '1' }}>
                    {safeVacationCount}
                  </h2>
                  <h6 className="mb-2 text-warning fw-bold">{t('vacation')}</h6>
                  <Badge 
                    bg="warning" 
                    className="px-2 py-1" 
                    style={{ fontSize: '0.9rem' }}
                  >
                    {vacationPercentage}%
                  </Badge>
                </div>
              </div>
            </div>
          </Col>

          {/* Right Side - Modern Enhanced Chart */}
          <Col xs={12} md={6} className="text-center">
            <div style={{ position: 'relative', height: '200px' }}>
              <Doughnut 
                data={getChartData()}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        usePointStyle: true,
                        padding: 20,
                        font: {
                          size: 13,
                          family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                          weight: 600
                        }
                      }
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#ffffff',
                      bodyColor: '#ffffff',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      borderWidth: 1,
                      cornerRadius: 8,
                      displayColors: true,
                      callbacks: {
                        title: function(tooltipItems) {
                          return tooltipItems[0].label;
                        },
                        label: function(context) {
                          const value = context.parsed;
                          const total = displayedTotal;
                          const percentage = total === 0 ? 0 : Math.round((value / total) * 100);
                          return t('tooltipLabel', { value, percentage });
                        }
                      }
                    }
                  },
                  cutout: '70%',
                  animation: {
                    animateRotate: true,
                    animateScale: true,
                    duration: 1500,
                    easing: 'easeOutQuart'
                  },
                  hover: {
                    mode: 'nearest',
                    intersect: true
                  }
                }}
                plugins={[modernCenterTextPlugin]}
              />
            </div>
          </Col>
        </Row>

        {/* Expandable Details */}
        <Collapse in={isExpanded}>
          <div className="mt-3">
            <h6 className="mb-2 text-muted" style={{ fontSize: '0.875rem' }}>{t('detailedInfo')}</h6>
            
            {/* Present Children Table */}
            <div className="mb-2">
              <h6 className="text-success mb-2" style={{ fontSize: '0.875rem' }}>
                <i className="bi bi-check-circle me-2"></i>
                {t('presentChildren', { count: safePresentCount })}
              </h6>
              <DetailList 
                type="present" 
                data={presentData}
                nurseryTimezone={nurseryTimezone}
              />
            </div>

            {/* Absent Children Table */}
            <div className="mb-2">
              <h6 className="text-warning mb-2" style={{ fontSize: '0.875rem' }}>
                <i className="bi bi-clock-history me-2"></i>
                {t('halfDayChildren', { count: safeHalfDayCount, defaultValue: `Half Day (${safeHalfDayCount})` })}
              </h6>
              <DetailList
                type="present"
                data={halfDayData}
                nurseryTimezone={nurseryTimezone}
              />
            </div>

            {/* Absent Children Table */}
            <div className="mb-2">
              <h6 className="text-danger mb-2" style={{ fontSize: '0.875rem' }}>
                <i className="bi bi-x-circle me-2"></i>
                {t('absentChildren', { count: safeAbsentCount })}
              </h6>
              <DetailList 
                type="absent" 
                data={absentData}
                nurseryTimezone={nurseryTimezone}
              />
            </div>

            {/* Vacation Children Table */}
            <div className="mb-2">
              <h6 className="text-warning mb-2" style={{ fontSize: '0.875rem' }}>
                <i className="bi bi-schedule-check me-2"></i>
                {t('vacationChildren', { count: safeVacationCount })}
              </h6>
              <DetailList 
                type="vacation" 
                data={vacationData}
                nurseryTimezone={nurseryTimezone}
              />
            </div>

            {/* Holiday Children Table */}
            <div className="mb-2">
              <h6 className="mb-2" style={{ fontSize: '0.875rem', color: '#009688' }}>
                <i className="bi bi-schedule-event me-2"></i>
                {t('holidayChildren', { count: safeHolidayCount })}
              </h6>
              <DetailList 
                type="holiday" 
                data={holidayData}
                nurseryTimezone={nurseryTimezone}
              />
            </div>

            </div>
        </Collapse>
      </div>
    </div>
  );
};

export default AttendanceChartWidget;
